import React from "react";
import type { ClinicalEvent } from "../types/clinicalEvent";
import "./ReviewAccelerationToolkit.css";

export type DensityBucket = {
  start: number;
  end: number;
  total: number;
};

export type ReviewAccelerationToolkitProps = {
  densityStripVisible: boolean;
  onToggleDensityStrip: () => void;
  densityBuckets: DensityBucket[];
  recordingDurationSeconds: number;
  currentTimeSeconds: number;
  bookmarks: ClinicalEvent[];
  clipQueue: ClinicalEvent[];
  selectedEventType: string | "all";
  eventTypeOptions: string[];
  baselineComparisonEnabled: boolean;
  onBookmarkCurrent: () => void;
  onRemoveBookmark: (eventId: string) => void;
  onEnqueueCurrentClip: () => void;
  onDequeueClip: (eventId: string) => void;
  onSelectEventType: (value: string | "all") => void;
  onToggleBaselineComparison: () => void;
  onJumpToEvent: (event: ClinicalEvent) => void;
  onSeek: (timeSeconds: number) => void;
};

export default function ReviewAccelerationToolkit({
  densityStripVisible,
  onToggleDensityStrip,
  densityBuckets,
  recordingDurationSeconds,
  currentTimeSeconds,
  bookmarks,
  clipQueue,
  selectedEventType,
  eventTypeOptions,
  baselineComparisonEnabled,
  onBookmarkCurrent,
  onRemoveBookmark,
  onEnqueueCurrentClip,
  onDequeueClip,
  onSelectEventType,
  onToggleBaselineComparison,
  onJumpToEvent,
  onSeek,
}: ReviewAccelerationToolkitProps) {
  const duration = Math.max(1, recordingDurationSeconds);
  const maxBucket = Math.max(1, ...densityBuckets.map((b) => b.total));

  return (
    <div className="review-accel" data-testid="review-acceleration-toolkit">
      <div className="eeg-dock-panel-chrome__toolbar review-accel__toolbar">
        <button type="button" onClick={onToggleDensityStrip}>
          {densityStripVisible ? "Hide density strip" : "Show density strip"}
        </button>
        <button type="button" onClick={onBookmarkCurrent}>
          Bookmark
        </button>
        <button type="button" onClick={onEnqueueCurrentClip}>
          Add to clip queue
        </button>
        <button type="button" onClick={onToggleBaselineComparison}>
          {baselineComparisonEnabled
            ? "Baseline comparison on"
            : "Baseline comparison off"}
        </button>
        <div
          className="review-accel__segmented"
          role="group"
          aria-label="Jump by event type"
        >
          <button
            type="button"
            className={selectedEventType === "all" ? "is-active" : ""}
            onClick={() => onSelectEventType("all")}
          >
            All
          </button>
          {eventTypeOptions.map((type) => (
            <button
              key={type}
              type="button"
              className={selectedEventType === type ? "is-active" : ""}
              onClick={() => onSelectEventType(type)}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      {densityStripVisible && (
        <div className="review-accel__density" aria-label="Event density strip">
          {densityBuckets.map((bucket) => {
            const left = (bucket.start / duration) * 100;
            const width = Math.max(
              0.4,
              ((bucket.end - bucket.start) / duration) * 100,
            );
            const height = Math.max(8, (bucket.total / maxBucket) * 100);
            return (
              <button
                key={`${bucket.start}-${bucket.end}`}
                type="button"
                className="review-accel__density-bar"
                style={{
                  left: `${left}%`,
                  width: `${width}%`,
                  height: `${height}%`,
                }}
                title={`${bucket.total} events @ ${bucket.start.toFixed(1)}s`}
                onClick={() => onSeek(bucket.start)}
              />
            );
          })}
          <div
            className="review-accel__playhead"
            style={{ left: `${(currentTimeSeconds / duration) * 100}%` }}
          />
        </div>
      )}

      <div className="review-accel__panels">
        <section aria-label="Bookmarks">
          <h4>Bookmarks</h4>
          {bookmarks.map((event) => (
            <div key={event.eventId} className="review-accel__item">
              <button type="button" onClick={() => onJumpToEvent(event)}>
                {event.startTimeSeconds.toFixed(1)}s · {event.eventLabel}
              </button>
              <button type="button" onClick={() => onRemoveBookmark(event.eventId)}>
                Remove
              </button>
            </div>
          ))}
          {!bookmarks.length && <p>No bookmarks yet.</p>}
        </section>

        <section aria-label="Clip queue drawer">
          <h4>Clip queue</h4>
          {clipQueue.map((event) => (
            <div key={event.eventId} className="review-accel__item">
              <button type="button" onClick={() => onJumpToEvent(event)}>
                {event.startTimeSeconds.toFixed(1)}s · {event.eventLabel}
              </button>
              <button type="button" onClick={() => onDequeueClip(event.eventId)}>
                Remove
              </button>
            </div>
          ))}
          {!clipQueue.length && <p>Clip queue is empty.</p>}
        </section>
      </div>
    </div>
  );
}
